/**
 * `/api/workflows/roadmap` — the blocked-workflow catalog (RO-3-13).
 *
 * ## Why this is a REWRITE
 *
 * `excellent/app/agents/roadmap/page.tsx` (145 LOC) calls `listBlockedLoops()`
 * and `knownRoleSlugs()` inline, then does the aggregation that gives the page
 * its reason to exist — "if I authored role X, how many workflows would
 * unblock?" — in the server render body, and declares its `DataTable` columns
 * (functions, which cannot cross a server→client boundary) in the same file.
 * None of that survives a mechanical port.
 *
 * ## Decision — the aggregation moves to the SERVER, not the client
 *
 * The original computes `unblockPotential` (a `Map<roleSlug, count>`) and then
 * the top-12 leverage list from the rows it already has. That could technically
 * be redone in the browser from `rows`, and it deliberately is NOT, for two
 * reasons:
 *
 *  1. `knownRoleSlugs()` reads `packages/agents/roles/*.md` off DISK. It is not
 *     derivable from the rows at all, and it is not something a browser can
 *     see. The page's headline ("N roles defined, M more needed") needs both
 *     halves, so the endpoint is already the only place both are in hand.
 *  2. The counts in the header and the counts in the leverage badges must
 *     agree. Computing one server-side and the other client-side is how they
 *     drift.
 *
 * So the endpoint returns the derived numbers as first-class fields and the
 * client route renders them. `rows` is still returned in full because the table
 * needs every blocked workflow with its own `missing_roles`.
 *
 * ## Decision — a NARROW row DTO, not `FleetLoopRow`
 *
 * `listBlockedLoops()` returns `FleetLoopRow`, which carries run counts, cost,
 * tokens, `active_version` and `last_run_at` — none of which this page renders,
 * and all of which are per-window figures that would invite a reader to compare
 * them against the fleet console's differently-windowed ones. The contract
 * publishes only the six fields the roadmap actually shows. A blocked workflow
 * has by definition never run, so the spend fields would all be zero anyway;
 * shipping them would be shipping a misleading zero.
 */

import { z } from "zod";
import type { Operation } from "../../types";

/** A workflow template blocked on role authoring — only what the roadmap renders. */
export interface BlockedWorkflowDto {
  id: number;
  slug: string;
  title: string;
  domain: string | null;
  step_count: number;
  /** Role slugs with no `packages/agents/roles/<slug>.md`. Never empty here. */
  missing_roles: string[];
}

/** "Authoring this role would unblock N workflows." */
export interface RoleLeverageDto {
  slug: string;
  unblocks: number;
}

export interface WorkflowRoadmap {
  rows: BlockedWorkflowDto[];
  /** Count of role files that DO exist under `packages/agents/roles/`. */
  knownRoleCount: number;
  /** Distinct roles still missing — the "N more needed" figure. */
  missingRoleCount: number;
  /** Highest-leverage missing roles, already sorted desc and capped at 12. */
  topMissing: RoleLeverageDto[];
}

const response = z.object({
  rows: z.array(
    z.object({
      id: z.number().int(),
      slug: z.string(),
      title: z.string(),
      domain: z.string().nullable(),
      step_count: z.number().int().nonnegative(),
      missing_roles: z.array(z.string()),
    }),
  ),
  knownRoleCount: z.number().int().nonnegative(),
  missingRoleCount: z.number().int().nonnegative(),
  topMissing: z.array(
    z.object({ slug: z.string(), unblocks: z.number().int().positive() }),
  ),
});

const getOp: Operation<void, void, void, WorkflowRoadmap> = {
  method: "GET",
  path: () => "/api/workflows/roadmap",
  response,
};

export const workflowRoadmap = {
  get: getOp,
};
